import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Star, X, Loader2, Check } from 'lucide-react';
import { formatRating, rateContent, RatingContentType, RatingSummary } from '../services/ratingService';

/** Nota estimada minimalista: "★ 4,3". Não renderiza nada sem nota. */
export const RatingBadge: React.FC<{ avg?: number | null; count?: number; className?: string; onDark?: boolean }> = ({ avg, count, className = '', onDark = false }) => {
  const { t, i18n } = useTranslation();
  if (!avg || avg <= 0) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono tabular-nums text-[11px] leading-none ${onDark ? 'text-white' : 'text-ink/70'} ${className}`}
      title={`${t('ratings.estimated', 'Nota estimada')}${count ? ` · ${t('ratings.count', { count, defaultValue: '{{count}} avaliações' })}` : ''}`}
    >
      <Star size={11} strokeWidth={1.75} className="fill-current text-[#D9B98A]" />
      {formatRating(avg, i18n.language)}
    </span>
  );
};

interface RatingModalProps {
  open: boolean;
  onClose: () => void;
  contentId: string;
  contentType: RatingContentType;
  title: string;
  author?: string;
  language?: string;
  initialRating?: number | null;
  onSaved?: (rating: number, summary: RatingSummary | null) => void;
}

/** Janela para dar nota de 1 a 5 estrelas (sem comentário). */
export const RatingModal: React.FC<RatingModalProps> = ({ open, onClose, contentId, contentType, title, author, language, initialRating, onSaved }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<number>(initialRating || 0);
  const [hover, setHover] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setValue(initialRating || 0); setHover(0); setDone(false); setError(null); }
  }, [open, initialRating]);

  if (!open) return null;

  const save = async () => {
    if (!value) return;
    setSaving(true);
    setError(null);
    try {
      const summary = await rateContent({ contentId, type: contentType, rating: value, title, author, language });
      setDone(true);
      onSaved?.(value, summary);
      setTimeout(onClose, 700);
    } catch (e: any) {
      setError(String(e?.message || '').includes('401') ? t('ratings.login_required', 'Entre na sua conta para avaliar.') : t('ratings.error', 'Não foi possível salvar a nota.'));
    } finally {
      setSaving(false);
    }
  };

  const shown = hover || value;

  return createPortal(
    <div className="fixed inset-0 z-[260] flex items-center justify-center p-4 bg-black/30 backdrop-blur-[20px] animate-in fade-in duration-200" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="bg-surface w-full max-w-sm rounded-[28px] p-6 border border-black/[0.06] dark:border-white/[0.06] animate-in zoom-in-95 duration-200 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-primary/[0.06] hover:bg-primary hover:text-slate-900 text-ink flex items-center justify-center transition-colors" aria-label="close">
          <X size={16} strokeWidth={1.75} />
        </button>
        <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">{t('ratings.rate', 'Avaliar')}</span>
        <h3 className="font-display text-xl font-medium text-ink tracking-[-0.02em] mt-1 pr-10 line-clamp-2">{title}</h3>
        <p className="text-sm text-muted mt-1">{t('ratings.prompt', 'Que nota você dá?')}</p>

        <div className="flex items-center justify-center gap-2 my-6" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onMouseEnter={() => setHover(n)}
              onClick={() => setValue(n)}
              className="w-11 h-11 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
              aria-label={t('ratings.stars', { count: n, defaultValue: '{{count}} estrelas' })}
            >
              <Star size={28} strokeWidth={1.5} className={n <= shown ? 'fill-[#D9B98A] text-[#D9B98A]' : 'text-ink/25'} />
            </button>
          ))}
        </div>

        {error && <p className="text-xs text-bad text-center mb-3">{error}</p>}

        <button
          onClick={save}
          disabled={!value || saving || done}
          className="w-full h-12 bg-primary text-slate-900 rounded-full text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : done ? <Check size={16} /> : null}
          {done ? t('ratings.saved', 'Nota salva') : t('ratings.save', 'Salvar nota')}
        </button>
      </div>
    </div>,
    document.body
  );
};
